import queryString from 'query-string';
import {
  getContractRequestQForAL,
  getBuyerDummyDataForAL
} from './graphql-queries.js';
import { pushLogoutEvent } from './googleTagManager';
import { callStaffFileMatchApi } from './api.learning';

const API_URL = process.env.REACT_APP_API_URL;
const CONTENTFUL_HELPSIDEBAR = process.env.REACT_APP_CONTENTFUL_HELPSIDEBAR;
const FILEMATCH_URL = process.env.REACT_APP_FILEMATCH_URL;
const HERMES_GATEWAY_URL = process.env.REACT_APP_HERMES_GATEWAY_URL;
const TI2_URL = process.env.REACT_APP_TI2_URL;
const API_CONTENTFUL = 'https://cdn.contentful.com/';

const jsonDecode = async response => {
  try {
    const jsonRet = await response.json();
    return jsonRet;
  } catch (jsonErr) {
    return {};
  }
}

export const callApi = async (
  endpoint,
  method = 'get',
  bodyParams = null,
  token = null,
  includeResponseHeaders = false,
  signal,
  credentials,
) => {
  let fullUrl = (/^https?:\/\/*/.test(endpoint)) ? endpoint : `${API_URL}${endpoint}`;
  const isMultipart = typeof FormData !== 'undefined' && bodyParams instanceof FormData;
  const body = (method.toLowerCase() !== 'get' && bodyParams)
    ? (isMultipart ? bodyParams : JSON.stringify(bodyParams))
    : null;
  if (method.toLowerCase() === 'get' && bodyParams) {
    const params = new URLSearchParams(bodyParams);
    if (params.toString()) {
      fullUrl = `${fullUrl}?${params.toString()}`;
    }
  }
  const jwt = token || localStorage.getItem('jwt') || '';
  let response;
  try  {
    response = await fetch(fullUrl, {
      method,
      ...(credentials ? { credentials } : {}),
      // credentials: 'same-origin',
      // mode: 'no-cors',
      headers: {
        Accept: 'application/json',
        ...(!isMultipart ? { 'Content-Type': 'application/json' } : {}),
        ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      },
      body,
      signal,
    });
  } catch (err) {
  console.log("err", err);
    response = err;
  }
  const json = await jsonDecode(response);
  if (!response.ok) {
    if (json.code === 'InvalidCredentials') {
      if (json.message.indexOf('jwt') > -1) {
        localStorage.removeItem('jwt');
      }
      pushLogoutEvent(() =>
        window.location.assign(
          `${process.env.REACT_APP_OLD_APP_URL}/signin?auth=true&reauth=true&redirect_to=${window.location.href}`
        )
      );
    }
    return {
      error: {
        response,
        json,
        request: {
          url: fullUrl,
          body,
          method,
        },
      },
    };
  }
  const newToken = response.headers.get('new-token');
  if (Boolean(newToken)) localStorage.setItem('jwt', newToken);

  return {
    response: json,
    ...(includeResponseHeaders ? { headers: response.headers } : {}),
  };
};
const callTi2Api = async (ti2token, endpoint, method = 'get', bodyParams = null) =>
  callApi(`${TI2_URL}${endpoint}`, method, bodyParams, ti2token);

const callFileMatchApi = async (endpoint, method = 'get', bodyParams = null, signal) => {
  return callApi(`${FILEMATCH_URL}${endpoint}`, method, bodyParams, null, false, signal);
};
const callFileMatchApiWithHeaders = async (endpoint, method = 'get', bodyParams = null) => {
  return callApi(`${FILEMATCH_URL}${endpoint}`, method, bodyParams, null, true);
};
const GONDOLA_INGEST_EMAIL_PAGE_SIZE = 250;
const GONDOLA_INGEST_EMAIL_MAX_PAGES = 1000;
const gondolaIngestEmailPaginationError = () => ({
  error: { message: 'Unable to load complete ingest email review data' },
});
const getAllGondolaIngestEmails = async (ingestId, companyId) => {
  const encodedIngestId = encodeURIComponent(ingestId);
  const encodedCompanyId = encodeURIComponent(companyId);
  const emails = [];
  let page = 1;
  let firstPayload = {};
  const seenEmailIds = new Set();
  let expectedTotalCount = null;
  let expectedTotalHeaderPresent = null;
  let expectedHasMoreHeaderPresent = null;

  while (true) {
    if (page > GONDOLA_INGEST_EMAIL_MAX_PAGES) {
      return gondolaIngestEmailPaginationError();
    }
    const result = await callFileMatchApiWithHeaders(
      `/gondola/ingests/${encodedIngestId}/emails?companyId=${encodedCompanyId}`
      + `&page=${page}&pageSize=${GONDOLA_INGEST_EMAIL_PAGE_SIZE}`,
      'get',
    );
    if (result.error) return result;

    const payload = result.response;
    if (
      !payload
      || typeof payload !== 'object'
      || Array.isArray(payload)
      || !Array.isArray(payload.emails)
    ) {
      return gondolaIngestEmailPaginationError();
    }
    const pageEmails = payload.emails;
    if (page === 1) firstPayload = payload;
    const pageEmailIds = pageEmails
      .map(email => email && typeof email.emailId === 'string' ? email.emailId : null)
      .filter(Boolean);
    const newEmailIds = pageEmailIds.filter(emailId => !seenEmailIds.has(emailId));
    if (
      pageEmailIds.length !== pageEmails.length
      || new Set(pageEmailIds).size !== pageEmailIds.length
      || newEmailIds.length !== pageEmailIds.length
    ) {
      return gondolaIngestEmailPaginationError();
    }
    pageEmailIds.forEach(emailId => seenEmailIds.add(emailId));
    emails.push(...pageEmails);

    const hasMoreHeader = result.headers && result.headers.get('x-has-more');
    const totalCountHeader = result.headers && result.headers.get('x-total-count');
    const normalizedHasMoreHeader = typeof hasMoreHeader === 'string'
      ? hasMoreHeader.trim().toLowerCase()
      : null;
    if (hasMoreHeader != null && !['true', 'false'].includes(normalizedHasMoreHeader)) {
      return gondolaIngestEmailPaginationError();
    }
    const normalizedTotalCountHeader = typeof totalCountHeader === 'string'
      ? totalCountHeader.trim()
      : '';
    const totalCount = Number(normalizedTotalCountHeader);
    const hasValidTotalCount = totalCountHeader != null
      && /^(0|[1-9]\d*)$/.test(normalizedTotalCountHeader)
      && Number.isSafeInteger(totalCount);
    if (totalCountHeader != null && !hasValidTotalCount) {
      return gondolaIngestEmailPaginationError();
    }
    const totalHeaderPresent = totalCountHeader != null;
    const hasMoreHeaderPresent = hasMoreHeader != null;
    if (page === 1) {
      expectedTotalHeaderPresent = totalHeaderPresent;
      expectedHasMoreHeaderPresent = hasMoreHeaderPresent;
      expectedTotalCount = hasValidTotalCount ? totalCount : null;
    } else if (
      expectedTotalHeaderPresent !== totalHeaderPresent
      || expectedHasMoreHeaderPresent !== hasMoreHeaderPresent
      || (hasValidTotalCount && totalCount !== expectedTotalCount)
    ) {
      return gondolaIngestEmailPaginationError();
    }
    const hasMore = normalizedHasMoreHeader === 'true'
      || (hasMoreHeader == null && pageEmails.length === GONDOLA_INGEST_EMAIL_PAGE_SIZE);
    if (hasValidTotalCount) {
      const totalSaysMore = emails.length < totalCount;
      if (emails.length > totalCount || hasMore !== totalSaysMore) {
        return gondolaIngestEmailPaginationError();
      }
    }
    if (hasMore && pageEmails.length === 0) {
      return gondolaIngestEmailPaginationError();
    }
    if (!hasMore || (hasValidTotalCount && emails.length === totalCount)) {
      return { response: { ...firstPayload, emails } };
    }
    if (page === GONDOLA_INGEST_EMAIL_MAX_PAGES) {
      return gondolaIngestEmailPaginationError();
    }
    page += 1;
  }
};
export const callApiForAL = async (endpoint, method = 'get', bodyParams = null) => {
  const fullUrl = /^https?:\/\/*/.test(endpoint) ? endpoint : API_URL + endpoint;
  const body = bodyParams ? JSON.stringify(bodyParams) : null;
  const hashtoken = sessionStorage.getItem('hashtoken') || '';
  let response;
  try  {
    response = await fetch(fullUrl, {
      method,
      // credentials: 'same-origin',
      // mode: 'no-cors',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        hashtoken
      },
      body
    });
  } catch (err) {
    response = err;
  }
  const json = await jsonDecode(response);
  if (!response.ok) {
    return {
      error: {
        response,
        json,
        request: {
          url: fullUrl,
          body,
          method,
        },
      },
    };
  }
  return { response: json };
}
// api services
export const account = {
  getAccountInfo: () => callApi(`/users`, 'get'),
  logout: async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      return await callApi('/logout', 'post', {}, null, false, controller.signal);
    } finally {
      clearTimeout(timeout);
    }
  },
  hydrateSettings: () => callApi('/settings/hydrate', 'get'),
  updateUser: payload => callApi('/user', 'put', payload),
  omniSearch: payload => callApi('/omni-search', 'post', payload),
  updateUIpreferences: payload => callApi('/updateUIpreferences', 'put', payload),
  signUpBookingAiUser: payload => callApi('/signUpBookingAiUser', 'post', payload),
  signUpGenerativeAiUser: payload => callApi('/signUpGenerativeAiUser', 'post', payload),
  // check if email is registered
  decryptEmail: payload => callApi(`/decryptEmail/${payload.email}`, 'get'),
  getBillingInfo: () => callApi(`/billing`),
  createSubscriptions: payload => callApi('/billing/subscriptions', 'post', payload),
  cancelSubscriptions: payload => callApi('/billing/subscriptions', 'delete', payload),
  getCouponInfo: ({ couponCode }) => callApi(`/coupon/${couponCode}`),
  joinAffiliate: () => callApi('/affiliate', 'post', {}),
  resetPassword: payload => callApi('/settings/reset', 'put', payload),
  updateUserSettings: payload => callApi('/settings/saveEditedUser', 'put', payload),
  deleteUser: payload => callApi(`/userManagement/editUser/${payload.userId}`, 'delete'),
  addUser: payload => callApi('/userManagement/addUser', 'put', payload),
  updatePaymentMethod: payload => callApi('/billing/source', 'put', payload),
  getInvoices: () => callApi(`/billing/invoices`, 'get'),
  createAffiliates: ({ hint, integrationId }) =>
    callApi('/ti2/affiliates', 'post', { hint, app: integrationId }),
};

export const generativeAi = {
  getAllCategories: () => {
    return callFileMatchApi('/generative-ai-asset-categories', 'get');
  },
  getAllAssets: ({ contentType }) => {
    return callApi(`/generative-ai/listContent/${contentType || 'vectorstore'}`, 'get');
  },
  uploadAsset: payload => {
    return callApi('/generative-ai/uploadContent', 'post', payload);
  },
  updateAsset: payload => {
    return callApi('/generative-ai/updateContent', 'put', payload);
  },
  deleteAsset: payload => {
    return callApi('/generative-ai/deleteContent', 'delete', payload);
  },
  removeIntegration: ({ integrationId, hint }) =>
    callApi(`/generative-ai/integrations/${encodeURIComponent(integrationId)}`, 'delete', {
      hint,
    }),
  getItineraries: payload => {
    return [];
  },
  getItinerary: id => {
    return [];
  },
  deleteItineray: id => {
    return [];
  },
  saveItinerary: payload => {
    return [];
  },
  saveAISettings: payload => {
    return callApi('/addin/saveSettings', 'post', payload);
  },
  sendPrompt: payload => {
    return callFileMatchApi('/vectorQA', 'post', payload);
  },
  vectorProcess: payload => callFileMatchApi('/vectorProcess', 'post', payload),
  fileProcess: payload => callFileMatchApi('/attachment/llmFileProcess', 'post', payload),
  checkVectorJobStatus: payload => callFileMatchApi(`/vectorProcess/status`, 'post', payload),
  generateQuote: payload => {
    return callFileMatchApi('/vectorQA', 'post', payload);
  },
  getPromptLibrary: params => callFileMatchApi(`/promptLibrary${params ? `?${queryString.stringify(params)}` : ''}`, 'get'),
  getChatThreads: emailIdOrTool => callApi(`/generative-ai/${emailIdOrTool}/chat-threads`, 'get'),
  openaiAssistant: payload => callFileMatchApi('/openaiAssistant', 'post', {
    ...payload,
    backendJwt: localStorage.getItem('jwt'),
  }),
  checkOpenaiRunStatusAndMessages: payload => callFileMatchApi('/openaiAssistant/status', 'post', {
    ...payload.jobs,
    backendJwt: localStorage.getItem('jwt'),
  }),
  deassocChatThread: tcThreadId => callApi(`/generative-ai/chat-thread/${tcThreadId}`, 'delete'),
  createRule: payload => callFileMatchApi('/ai-rules', 'post', payload),
  getStagingAIRules: payload => {
    return callFileMatchApi('/staging-ai-rules', 'get', payload);
  },
  updateStagingAIRules: payload => {
    return callFileMatchApi('/staging-ai-rules', 'put', payload);
  },
  getLLMParser: payload => callFileMatchApi(`/LLMParser?toolId=${payload.toolId}&partnerId=${payload.partnerId || ''}&promptStrategy=${payload.promptStrategy || ''}`, 'get'),
  saveLLMParser: payload => callFileMatchApi('/LLMParser', 'post', payload),
  getLLMRules: payload => callFileMatchApi(`/LLMRules?toolId=${payload.toolId}`, 'get'),
  getLLMProviders: () => callFileMatchApi('/ai-settings/llm/providers', 'get'),
  getLLMConfig: ({ toolId, partnerId }) => callFileMatchApi(`/ai-settings/llm?toolId=${toolId}&partnerId=${partnerId || ''}`, 'get'),
  saveLLMConfig: payload => callFileMatchApi('/ai-settings/llm', 'post', payload),
  getOpenVikingConfig: () => callFileMatchApi('/ai-settings/openviking', 'get'),
  saveOpenVikingConfig: payload => callFileMatchApi('/ai-settings/openviking', 'post', payload),
  getHermesEnvironment: () => callFileMatchApi('/ai-settings/hermes', 'get'),
  setHermesEnvironment: enabled => callFileMatchApi('/ai-settings/hermes', 'post', { enabled }),
  setHermesSkillsReady: hermesSkillsReady => callFileMatchApi('/ai-settings/hermes', 'post', { hermesSkillsReady }),
  getItineraryRuntime: () => callFileMatchApi('/ai-settings/hermes/itinerary-runtime', 'get'),
  setItineraryRuntime: itineraryRuntime => callFileMatchApi('/ai-settings/hermes/itinerary-runtime', 'post', { itineraryRuntime }),
  openHermesSession: async () => {
    if (!HERMES_GATEWAY_URL) return { error: { json: { error: 'Company chat is not configured for this app.' } } };
    try {
      const response = await fetch(`${HERMES_GATEWAY_URL}/api/session`, {
        method: 'POST',
        credentials: 'include',
        headers: { Authorization: `Bearer ${localStorage.getItem('jwt') || ''}` },
      });
      const body = await jsonDecode(response);
      if (!response.ok) return { error: { json: body } };
      const chatUrl = new URL(body.chatUrl, HERMES_GATEWAY_URL);
      if (chatUrl.origin !== new URL(HERMES_GATEWAY_URL).origin) {
        return { error: { json: { error: 'Company chat returned an invalid destination.' } } };
      }
      return { response: { chatUrl: chatUrl.href } };
    } catch (error) {
      return { error: { json: { error: 'Company chat is unavailable.' } } };
    }
  },
  getLearningStatus: (companyId, signal) => callFileMatchApi(`/ai-settings/learning?${queryString.stringify({ companyId })}`, 'get', null, signal),
  setupLearning: (companyId, body) => callFileMatchApi(`/ai-settings/learning/setup?${queryString.stringify({ companyId })}`, 'post', body),
  prepareLearningRecovery: (operationId, body) => callFileMatchApi(`/ai-settings/learning/operations/${encodeURIComponent(operationId)}/recovery`, 'post', body),
  getLearningRecovery: (recoveryId, companyId) => callFileMatchApi(`/ai-settings/learning/actions/${encodeURIComponent(recoveryId)}?${queryString.stringify({ companyId })}`),
  confirmLearningRecovery: (recoveryId, expectedActionDigest, companyId) => callFileMatchApi(`/ai-settings/learning/actions/${encodeURIComponent(recoveryId)}/confirm?${queryString.stringify({ companyId })}`, 'post', { expectedActionDigest }),
  getLearningHistory: (filters, signal) => callFileMatchApi(`/ai-settings/learning/history?${queryString.stringify(filters)}`, 'get', null, signal),
  getAgentSkills: companyId => callFileMatchApi(`/ai-settings/skills?${queryString.stringify({ companyId })}`),
  saveAgentSkillsSettings: (enabled, expectedGeneration, companyId) => callStaffFileMatchApi('/ai-settings/skills/settings', companyId, 'PUT', { enabled, expectedGeneration }),
  checkoutAgentSkill: (name, companyId) => callFileMatchApi(`/ai-settings/skills/${encodeURIComponent(name)}/checkout?${queryString.stringify({ companyId })}`),
  uploadAgentSkill: (file, replace = false, { companyId, expectedGeneration, expectedSha256 = null } = {}) => {
    const payload = new FormData();
    payload.append('file', file);
    const guards = {
      expectedGeneration,
      expectedSha256: expectedSha256 === null ? 'null' : expectedSha256,
      ...(replace ? { replace: true } : {}),
    };
    return callStaffFileMatchApi('/ai-settings/skills', companyId, 'POST', payload, guards);
  },
  deleteAgentSkill: (name, expected, companyId) => callStaffFileMatchApi(`/ai-settings/skills/${encodeURIComponent(name)}`, companyId, 'DELETE', expected),
  apiKeysloadAllKeys: payload => callApi('/generative-api/apikeys', 'get', payload),
  apiKeysGenerate: payload => callApi('/generative-api/apikeys', 'post', payload),
  apiKeysRemove: id => callApi('/generative-api/apikeys', 'delete', { id }),
  importFilters: payload => callFileMatchApi('/generative-ai/import-filters', 'post', payload),
  getIAUsageReportV2: payload => callApi('/generative-ai/reportsv2', 'get', payload),
  getIATourplanSyncStatus: () => callApi('/generative-ai/reportsv2/tourplan-sync', 'get'),
  startIATourplanSync: () => callApi('/generative-ai/reportsv2/tourplan-sync', 'post'),
  vectordbStatusCheck: payload => callFileMatchApi('/vectordb/status-check', 'post', payload),
  refreshStores: payload => callFileMatchApi('/vectordb/refresh', 'post', payload),
  itineraryMiningRun: payload => callFileMatchApi('/generative-ai/itinerary-mining/enrich', 'post', payload),
  itineraryMiningStatus: payload => callFileMatchApi('/generative-ai/itinerary-mining/enrich/status', 'post', payload),
  itineraryMiningCancel: payload => callFileMatchApi('/generative-ai/itinerary-mining/enrich/cancel', 'post', payload),
  itineraryMiningClear: payload => callFileMatchApi('/generative-ai/itinerary-mining/enrich/clear', 'post', payload),
  itineraryMiningLastQuery: payload =>
    callFileMatchApi('/generative-ai/itinerary-mining/enrich/last-query', 'get', payload),
  gondolaGetScopes: companyId => callFileMatchApi(`/gondola/scopes?companyId=${encodeURIComponent(companyId)}`, 'get'),
  gondolaGetScopeDetail: scopeId => callFileMatchApi(`/gondola/scope/${encodeURIComponent(scopeId)}`, 'get'),
  gondolaGetScopeMapping: scopeId => callFileMatchApi(`/gondola/scope/${encodeURIComponent(scopeId)}/mapping`, 'get'),
  gondolaGetScopeRuns: (scopeId, limit = 30, cursor) => {
    const params = [`limit=${encodeURIComponent(limit)}`];
    if (cursor) params.push(`cursor=${encodeURIComponent(cursor)}`);
    return callFileMatchApi(`/gondola/scope/${encodeURIComponent(scopeId)}/runs?${params.join('&')}`, 'get');
  },
  gondolaGetScopeFiles: scopeId => callFileMatchApi(`/gondola/scope/${encodeURIComponent(scopeId)}/files`, 'get'),
  gondolaGetScopeCounts: scopeId => callFileMatchApi(`/gondola/scope/${encodeURIComponent(scopeId)}/counts`, 'get'),
  gondolaCreateScope: data => callFileMatchApi('/gondola/scopes/create', 'post', data),
  gondolaInitIngest: data => callFileMatchApi('/gondola/ingests/init', 'post', data),
  gondolaGetIngestUploadUrls: data => callFileMatchApi('/gondola/ingests/upload-urls', 'post', data),
  gondolaStartIngest: (ingestId, data) => callFileMatchApi(`/gondola/ingests/${ingestId}/start`, 'post', data),
  gondolaCancelIngest: (ingestId, data) => callFileMatchApi(`/gondola/ingests/${encodeURIComponent(ingestId)}/cancel`, 'post', data),
  gondolaGetIngests: companyId => callFileMatchApi(`/gondola/ingests?companyId=${encodeURIComponent(companyId)}`, 'get'),
  gondolaGetIngestPartners: (ingestId, companyId) => callFileMatchApi(`/gondola/ingests/${ingestId}/partners?companyId=${encodeURIComponent(companyId)}`, 'get'),
  gondolaGetIngestEmails: (ingestId, companyId) => getAllGondolaIngestEmails(ingestId, companyId),
  gondolaGetIngestEmailDetail: (ingestId, emailId, companyId) => callFileMatchApi(`/gondola/ingests/${encodeURIComponent(ingestId)}/emails/${encodeURIComponent(emailId)}?companyId=${encodeURIComponent(companyId)}`, 'get'),
  gondolaSearchIngestEmail: (ingestId, emailId, data) => callFileMatchApi(`/gondola/ingests/${encodeURIComponent(ingestId)}/emails/${encodeURIComponent(emailId)}/search`, 'post', data),
  gondolaSelectIngestEmail: (ingestId, emailId, data) => callFileMatchApi(`/gondola/ingests/${encodeURIComponent(ingestId)}/emails/${encodeURIComponent(emailId)}/select`, 'post', data),
  gondolaAssignPartners: (ingestId, data) => callFileMatchApi(`/gondola/ingests/${ingestId}/assign`, 'post', data),
  gondolaGetUploadUrls: ({ contextScope, filenames }) => callFileMatchApi('/gondola/upload-urls', 'post', { contextScope, filenames }),
  gondolaStartRun: data => callFileMatchApi('/gondola/run', 'post', data),
  gondolaRunDeltaSync: data => callApi('/generative-ai/gondola/delta-sync/run', 'post', data),
  gondolaGetDeltaSyncStatus: () => callApi('/generative-ai/gondola/delta-sync/status', 'get'),
  gondolaRunBatchPromotion: data => callApi('/generative-ai/gondola/promotion/run', 'post', data),
  gondolaGetBatchPromotionStatus: () => callApi('/generative-ai/gondola/promotion/status', 'get'),
  gondolaStopRun: (runId, contextScope) => callFileMatchApi(
    `/gondola/run/${runId}/stop?contextScope=${encodeURIComponent(contextScope)}`,
    'post',
    null,
  ),
  gondolaForceStopRun: (runId, contextScope) => callFileMatchApi(
    `/gondola/run/${encodeURIComponent(runId)}/force-stop?contextScope=${encodeURIComponent(contextScope)}`,
    'post',
    null,
  ),
  gondolaPromoteRun: (runId, contextScope) => callFileMatchApi(
    `/gondola/run/${encodeURIComponent(runId)}/promote?contextScope=${encodeURIComponent(contextScope)}`,
    'post',
    null,
  ),
  gondolaDiscardRun: (runId, contextScope) => callFileMatchApi(
    `/gondola/run/${encodeURIComponent(runId)}/discard?contextScope=${encodeURIComponent(contextScope)}`,
    'post',
    null,
  ),
  gondolaDeltaCount: contextScope => callFileMatchApi(`/gondola/delta-count?contextScope=${encodeURIComponent(contextScope)}`, 'get'),
  gondolaGetRunDetail: (scopeId, runId) =>
    callFileMatchApi(`/gondola/scope/${encodeURIComponent(scopeId)}/run/${encodeURIComponent(runId)}`, 'get'),
  gondolaGetRunArtifacts: (scopeId, runId, iteration, file) => {
    let url = `/gondola/scope/${encodeURIComponent(scopeId)}/run/${encodeURIComponent(runId)}/artifacts`;
    const params = [];
    if (iteration != null) params.push(`iteration=${iteration}`);
    if (file) params.push(`file=${encodeURIComponent(file)}`);
    if (params.length) url += `?${params.join('&')}`;
    return callFileMatchApi(url, 'get');
  },
  gondolaAddScopeAssociation: data => callFileMatchApi('/gondola/scopes/associations', 'post', data),
  gondolaRemoveScopeAssociation: data => callFileMatchApi('/gondola/scopes/associations', 'delete', data),
  gondolaAddScopeTi2: data => callFileMatchApi('/gondola/scopes/ti2', 'post', data),
  gondolaRemoveScopeTi2: data => callFileMatchApi('/gondola/scopes/ti2', 'delete', data),
  gondolaSeedDefaultScopeTi2: ({ integrationId, hint }) =>
    callFileMatchApi('/gondola/default-scope/ti2/seed', 'post', {
      app: integrationId,
      hint,
    }),
  productSyncGetInstances: () =>
    callFileMatchApi('/gondola/product-sync/instances', 'get'),
  productSyncGetInstance: instanceKey =>
    callFileMatchApi(`/gondola/product-sync/instances/${encodeURIComponent(instanceKey)}`, 'get'),
  productSyncStartRun: (instanceKey, data) =>
    callFileMatchApi(
      `/gondola/product-sync/instances/${encodeURIComponent(instanceKey)}/runs`,
      'post',
      data,
    ),
  productSyncUpdateAutoSync: (instanceKey, data) =>
    callFileMatchApi(
      `/gondola/product-sync/instances/${encodeURIComponent(instanceKey)}/auto-sync`,
      'patch',
      data,
    ),
  productSyncInspectChroma: instanceKey =>
    callFileMatchApi(
      `/gondola/product-sync/instances/${encodeURIComponent(instanceKey)}/chroma-inspection`,
      'post',
    ),
  getProductFilesStatus: ({ integrations }) =>
    callFileMatchApi('/gondola/product-files/status', 'post', { integrations }),
  gondolaCopyScope: data => callFileMatchApi('/gondola/scopes/copy', 'post', data),
  gondolaDeleteScope: data => callFileMatchApi('/gondola/scopes/delete', 'post', data),
  getScopeFile: (scopeId, fileName) =>
    callFileMatchApi(`/gondola/scope/${encodeURIComponent(scopeId)}/files/${encodeURIComponent(fileName)}`, 'get'),
  saveScopeFile: (scopeId, fileName, content) =>
    callFileMatchApi(`/gondola/scope/${encodeURIComponent(scopeId)}/files/${encodeURIComponent(fileName)}`, 'put', { content }),
};

export const reports = {
  exportIAXLSX: (payload) => callFileMatchApi('/reports/exportIAXLSX', 'post', {
    ...payload,
  }),
}

export const aiRules = {
  getAliases: () => callApi('/addin/alias', 'get'),
  deleteAlias: payload => callApi('/addin/alias', 'delete', payload),
  addAlias: payload => callApi('/addin/alias', 'post', payload),
  createRule: payload => callFileMatchApi('/ai-rules', 'post', payload),
  updateRule: ({ id, ...payload }) => callFileMatchApi(`/ai-rules/${id}`, 'put', payload),
  getAIRules: () => callFileMatchApi('/ai-rules', 'get'),
  deleteAIRule: id => callFileMatchApi(`/ai-rules/${id}`, 'delete'),
  productRuleEval: payload => callFileMatchApi('/evalMatch', 'post', payload),
}
export const ti2 = {
  createToken: () => callApi('/ti2/token', 'post'),
  getAllAppKeys: ({ companyId, ti2token }) => callTi2Api(ti2token, `/user/${companyId}/apps`, 'get'),
  getAppKey: ({ app, hint, ti2token }) => callFileMatchApi('/getTI2TokenInfo', 'post', {
    app,
    hint,
    ti2token,
  }),
  updateAppKey: ({ companyId, app, hint, ti2token, configuration }) => callTi2Api(ti2token, `/${app}/${companyId}`, 'put', { hint, configuration }),
  getProducts: ({ companyId, ti2token, app, hint, searchInput, optionId }) => callTi2Api(ti2token, `/products/${app}/${companyId}/${hint}/search`, 'post', {
    searchInput,
    optionId,
  }),
  forceRefreshProducts: ({ companyId, ti2token, app, integrationId, hint }) =>
    callTi2Api(ti2token, `/products/${integrationId || app}/${companyId}/${hint}/search`, 'post', {
      forceRefresh: true,
      backgroundJob: true,
      fullSyncTrigger: 'manual',
      admissionOverrideReason: 'TourConnect app integration product sync',
    }),
  getProductRefreshJob: ({ companyId, ti2token, integrationId, jobId }) =>
    callTi2Api(
      ti2token,
      `/${encodeURIComponent(integrationId)}/${encodeURIComponent(companyId)}/${encodeURIComponent(jobId)}/jobStatus`,
      'get',
    ),
  getAffiliateAgents: ({ companyId, ti2token, app, hint }) =>
    callTi2Api(ti2token, `/affiliate/${app}/${companyId}/${hint}/agents`, 'post', {}),
  saveIntegration: ({ companyId, integrationId, hint, ti2token, token }) =>
    callTi2Api(ti2token, `/${integrationId}/${companyId}`, 'post', {
      tokenHint: hint,
      token,
    }),
  validateIntegration: ({ companyId, integrationId, hint, ti2token }) =>
    callTi2Api(ti2token, `/${integrationId}/${companyId}/validate`, 'post', {
      tokenHint: hint,
    }),
  getIntegrationTemplate: ({ integrationId, ti2token }) =>
    callTi2Api(ti2token, `/app/${integrationId}/tokenTemplate`, 'get'),
  getCompanyIntegrationSettings: ({ companyId, integrationId, ti2token }) =>
    callTi2Api(ti2token, `/settings/${integrationId}/${companyId}`, 'get'),
  saveCompanyIntegrationSettings: ({ companyId, integrationId, settings, ti2token }) =>
    callTi2Api(ti2token, `/settings/${integrationId}/${companyId}`, 'post', { settings }),
  loadIntegrationToken: ({ companyId, integrationId, hint, ti2token }) =>
    callTi2Api(
      ti2token,
      `/${integrationId}/${companyId}/${encodeURIComponent(hint)}/token`,
      'get'
    ),
}
export const matcher = {
  checkStatus: payload => callFileMatchApi(`/mail/jobStatus?jobId=${payload.jobs[0].jobId}`, 'post', payload),
};

export const notifications = {
  getStatusMessage: () => callApi('/statusMessages'),
  getNFNotifications: () => callApi('/notifications'),
  markNFAsRead: activityIds =>
    callApi('/notifications', 'put', { activityIds }),
  acceptConnection: connectionId =>
    callApi(`/connections/${connectionId}/accepted`, 'put'),
  rejectConnection: connectionId =>
    callApi(`/connections/${connectionId}/rejected`, 'put'),
  markAcceptedRead: connectionIds =>
    callApi('/statusMessages/markConnectionAcceptedAsRead', 'post', {
      connectionIds
    }),
  sendNotification: payload => callApi('/sendNotification', 'post', payload)
};
export const partnerManagement = {
  getTags: () => callApi('/partner-management/tags', 'get'),
  saveTagChanges: (payload = {}) =>
    callApi('/partner-management/tags', 'post', {
      ...payload,
      items: payload.items.map(item => ({
        ...item,
        partnerId: item.type === 'partner' ? item.id : undefined,
        contactId: item.type === 'contact' ? item.id : undefined
      }))
    }),
  getFields: () => callApi('/partner-management/fields', 'get'),
  getPartners: (searchParams = {}) =>
    callApi('/partner-management/partners', 'post', searchParams),
  getContacts: (searchParams = {}) =>
    callApi('/partner-management/contacts', 'post', searchParams),
  createOnePartner: (payload = {}) =>
    callApi('/partner-management/partner', 'post', payload),
  mergePartners: (payload = {}) =>
    callApi('/partner-management/merge-partners', 'put', payload),
  archivePartners: ({ partnerIds, archiveContactsBool }) =>
    callApi('/partner-management/archive-partner', 'put', {
      partnerIds,
      archiveContactsBool
    }),
  archiveContacts: ({ contactIds }) =>
    callApi('/partner-management/archive-contacts', 'put', {
      contactIds
    }),
  unarchivePartner: ({ partnerId }) =>
    callApi('/partner-management/unarchive-partner', 'put', { partnerId }),
  unarchiveContact: ({ contactId }) =>
    callApi('/partner-management/unarchive-contact', 'put', { contactId }),
  exportCSV: ({ items, headers }) =>
    callApi('/partner-management/export', 'post', { items, headers }),
  importFile: payload =>
    callApi('/partner-management/import', 'post', payload),
  sendAssets: payload =>
    callApi('/partner-management/send-assets', 'post', payload),
  getBounceList: () =>
    callApi('/partner-management/bounce-list', 'get'),
  markDoneFromBounceList: payload =>
    callApi('/partner-management/bounce-list', 'put', { contactIds: payload.contactIds }),
  createNewProperty: payload =>
    callApi('/partner-management/settings/property', 'post', payload),
  updateProperty: payload =>
    callApi('/partner-management/settings/property', 'put', payload),
  archiveProperty: payload =>
    callApi('/partner-management/settings/property', 'delete', payload),
  createNewTemplate: payload =>
    callApi('/partner-management/message-templates', 'post', payload),
  getAllTemplates: () =>
    callApi('/partner-management/message-templates', 'get'),
  updateATemplate: payload =>
    callApi('/partner-management/message-templates', 'put', payload),
  deleteATemplate: payload =>
    callApi('/partner-management/message-templates', 'delete', payload),
  updateSignature: payload =>
    callApi('/partner-management/message-signature', 'put', payload),
};

export const partner = {
  getPartner: ({ partnerId }) => callApi(`/partner/${partnerId}`, 'get'),
  updatePartnerDetails: ({ partnerId, ...extra }) =>
    callApi(`/partner/${partnerId}`, 'put', extra),
  getActivities: ({ assetId, activityType }) =>
    callApi(
      `/asset-activities/${assetId}?activityType=${activityType || ''}`,
      'get'
    ),
  addConnectionAsPartner: ({ connectionId, targetCompanyId }) =>
    callApi(`/partner-management/partner/connection`, 'post', {
      connectionId,
      targetCompanyId
    }),
  saveContacts: (payload = {}) =>
    callApi(`/partner/${payload.partnerId}/contacts`, 'put', {
      contacts: payload.contacts,
      deletedContacts: payload.deletedContacts
    }),
  unassocAMessage: ({ messageId, assetId }) =>
    callApi(`/asset-activity/${messageId}`, 'put', { assetId })
};

export const contact = {
  getContact: ({ contactId }) =>
    callApi(`/partner-management/contact/${contactId}`, 'get'),
  updateContactDetails: ({ contactId, ...payload }) =>
    callApi(`/partner-management/contact/${contactId}`, 'put', payload),
  associatePartner: payload =>
    callApi(`/partner-management/contact/${payload.contactId}/associatePartner`, 'put', payload),
};

export const contracting = {
  getContracts: ({ query, includeWorkspacesIfBuyer = false }) => {
    const qs = query
      ? queryString.stringify({
          additionalQuery: JSON.stringify(query),
          includeWorkspacesIfBuyer
        })
      : '';
    return callApi(`/contracting/contracts?${qs}`);
  },
  contractFromHF: payload =>
    callApi(
      `contracting/contractIdFromHistoryFeedId/${payload.historyFeedId}`,
      'post',
      payload
    ),
  unassocSingleActivity: ({ feedIndex, historyFeedId }) =>
    callApi('/contracting/contract/unassocSingleActivity', 'delete', {
      feedIndex,
      historyFeedId
    }),
  getTemplates: additionalQuery =>
    callApi(
      `/contracting/templates?${
        additionalQuery
          ? queryString.stringify({
              additionalQuery: JSON.stringify(additionalQuery)
            })
          : ''
      }`
    ),
  getWorkspaceList: payload => callApi(`/contracting/workspace-list`, 'post', payload),
  getContractList: payload => callApi('/contracting/buyer-contract-list', 'post', payload),
  createFolder: payload => callApi('/contracting/contract-list/folder', 'post', payload),
  getAllFolders: () => callApi('/contracting/contract-list/folder', 'get'),
  // updatePartnerTags
  updateFolders: payload => callApi('/contracting/contract-list/folder', 'put', payload),
  deleteFolder: payload => callApi('/contracting/contract-list/folder', 'delete', payload),
  updateWorkspaceContract: payload => callApi('/contracting/workspace-contract', 'put', payload),
  assignFolders: payload => callApi('/contracting/assignFolder', 'put', payload),
  archiveOrUnArchive: payload => callApi('/contracting/archiveOrUnarchive', 'put', payload),
  deleteOrUndelete: payload => callApi('/contracting/deleteOrUndelete', 'put', payload),
  sendRequest: payload => callApi(`/contracting/resendContract/${payload.contractId}`, 'put', payload),
  bulkResendContracts: payload => callApi('/contracting/bulkResendContracts', 'put', payload),
  mergeWorkspaces: payload => callApi('/contracting/workspaces/merge', 'put', payload),
  loadDataForIframe: payload => {
    return callApi(`/contracting/getContractInfo/${payload.rateDocId}`, 'post', {
      query: `
          query getContract ($contractId: ID!, $collection: String!, $viewerId: ID!, $viewBy: String!, $realActorEmail: String) {
            DraftnTemplate (id: $contractId, collection: $collection, viewerId: $viewerId, viewBy: $viewBy) {
              completionPercent
              acceptedBy
              crIds
              ownerIds
              defaultInvitees
              magicLink (realActorEmail: $realActorEmail)
              signatures {
                 invitee
                 inviteSentAt
                 signedBy
                 signedAt
              }
              originFrom
              draftType
              editHistory {
                detail
                productId
                ratePackageName
              }
            }
          }
        `,
      contractId: payload.rateDocId,
      collection: payload.collection,
    });
  },
  getDiffReport: ({ docs, onlyGetSummary }) => callApi(`/contracting/report/diff/${docs}?onlyGetSummary=${onlyGetSummary}`, 'get'),
  acceptDummyTemplate: payload => callApi('/contracting/dummy/template/accept', 'put', payload),
  sendRCMsg: payload => callApi('/contracting/send-rc-msg', 'post', payload),
};

export const admin = {
  resendWorkspaceMessage: payload => callApi('/admin/resendWorkspaceMessage', 'put', payload),
  checkToken: ({ token }) => callApi('/admin/checkToken', 'post', { token }),
  search: ({
    token,
    searchString,
    searchCategory = 'All',
  }) =>  callApi(`/admin/search`, 'get', {
    searchString,
    searchCategory,
  }, token),
  getCompany: ({
    token,
    companyId,
  }) =>  callApi(`/admin/company/${companyId}`, 'get', {}, token),
  getLogs: ({
    token,
    id,
    type,
    queryParams
  }) =>  callApi(`/admin/logs/${type}/${id}`, 'get', queryParams, token),
};

export const bcmBilling = {
  // App-authenticated /billing routes; blackbird-api enforces TC admin access.
  getBCMBillingRuns: ({ startDate, endDate } = {}) => callApi('/billing/bcm-billing-runs', 'get', {
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
  }),
  approveBCMBillingRun: ({ id }) => callApi(`/billing/bcm-billing-runs/${id}/approve`, 'post'),
};

export const travelML = {
  getStopSaleExports: payload =>
    callApi(
      `/addin/export?${payload ? queryString.stringify(payload) : ''}`,
      'get'
    ),
  getOfferExports: payload =>
    callApi(
      `/addin/exportOffer?${payload ? queryString.stringify(payload) : ''}`,
      'get'
    ),
  updateEntityLevels: payload => callApi(`/addin/levels`, 'put', payload),
  getAllCollection: ({ rowsPage, rowsPerPage, rowsEntityName }) => {
    const offset = rowsPage * rowsPerPage;
    const params = new URLSearchParams({
      offset,
      limit: rowsPerPage,
      ...(rowsEntityName ? { 'elementName.match': rowsEntityName } : {})
    });
    return callApi(`/addin/collection?${params.toString()}`, 'get');
  },
  addCollection: payload => callApi(`/addin/collection`, 'post', payload),
  removeCollectionItem: payload =>
    callApi(`/addin/collection`, 'delete', payload),
  updateACollectionRow: payload => callApi(`/addin/collection`, 'put', payload),
  deleteARowFromCollection: payload =>
    callApi(`/addin/collection`, 'delete', payload),
  retryStopSale: payload => callApi('/addin/retryStopSale', 'post', payload),
  lookupMeta: ({ value, parentId }) => {
    const params = new URLSearchParams({
      limit: 1,
      elementId: value,
      parentId
    });
    return callApi(`/addin/collection?${params.toString()}`, 'get');
  }
};

export const assetsLanding = {
  retrieveTokenPayload: hashToken =>
    callApiForAL(`/verifyToken/${hashToken}`, 'post', {}),
  getContractRequestInfo: ({ requestId }) => {
    return callApiForAL(`/landing/getContractInfo/${requestId}`, 'post', {
      query: getContractRequestQForAL,
      contractId: requestId,
      collection: 'contract'
    });
  },
  getBuyerDummyData: ({ requestId }) => {
    return callApiForAL(`/landing/getContractInfo/${requestId}`, 'post', {
      query: getBuyerDummyDataForAL,
      contractId: requestId,
      collection: 'contract'
    });
  },
  getSupplierDataForALRequest: payload =>
    callApiForAL(`/landing/getSupplierDataForAL`, 'post', payload),
  getRespondedContracts: payload =>
    callApiForAL(
      `/contracting/request/${payload.requestId}/contracts?al=true`,
      'get'
    ),
  createRateSheets: payload =>
    callApiForAL(`/landing/createRateSheets`, 'post', payload),
  submitTCRates: payload =>
    callApiForAL('/landing/submitTCRates', 'post', payload),
  createEditDraft: payload =>
    callApiForAL(`/contracting/draft/${payload.contractId}`, 'post', payload),
  sendExternalRates: payload =>
    callApiForAL('/landing/sendExternalRates', 'post', payload),
  supplierSignup: payload =>
    callApiForAL('/landing/supplierSignup', 'post', {
      password: payload.password,
      companyName: payload.companyName
    })
};

export const workspace = {
  conversationsRead: ({ assetId, assetType = 'partner' }) =>
    callApi(`/workspace/markRead`, 'post', { assetId, assetType }),
  addANote: ({ partnerIds, contactIds, messageText, files }) =>
    callApi(`/workspace/newMessage`, 'post', {
      partnerIds,
      contactIds,
      type: 'note',
      messageText,
      files
    })
};

export const MMQuery = `query getMyOwnMM ($companyId: ID!) {
  companyProfile  (id: $companyId) {
    media {
      url
      mediaType
      created_at
      name
      size
      storageProvider
      username
      password
      tags
    }
    locations {
      name
      id
      products {
        id
        name
      }
    }
  }
}`.replace(/ +(?= )/g, '');

export const marketing = {
  getMarketingMaterials: () => callApi(`/profile?query=${MMQuery}`)
};

export const downloads = {
  downloadTCContractingDoc: payload => {
    // window.open(fileUrls.getDownloadTCContractingDoc(payload));
    const downloadUrl = fileUrls.getDownloadTCContractingDoc(payload);
    const a = document.createElement('a');
    a.download = true;
    a.target = '_blank';
    a.setAttribute('download', true);
    a.href = downloadUrl;
    a.dispatchEvent(new MouseEvent('click'));
  },
  getXls: (docType, docId, exportType) => callApi(`contracting/exportXls/${docType}/${docId}?type=${exportType}`, 'get'),
};
export const fileUrls = {
  getDownloadUrlSync: path => {
    if (path && path.indexOf('/') > -1) return `/s3/file?key=${path}`;
    return `/filebyhash/${path}`;
  },
  getDownloadTCContractingDoc: ({ docId, collection = 'contract', locale, ratesOnly, downloadRateFormat, forAL }) => {
    let url;
    if (forAL) {
      url = (downloadRateFormat || '').split('_')[0] === 'quickview'
        ? '/landingproxy/contracting/pipeXls' : `/landingproxy/download-pdf`;
    } else {
      url = (downloadRateFormat || '').split('_')[0] === 'quickview'
        ? '/proxy/contracting/pipeXls' : `/proxy/download-pdf`;
    }
    const qs = queryString.stringify({
      docId,
      locale,
      collection,
      ratesOnly: downloadRateFormat === 'condensed' || ratesOnly,
      type: (downloadRateFormat || '').indexOf('quickview') > -1
        ? (downloadRateFormat || '').split('_')[1] : '',
    });
    return (`${url}?${qs}`);
  },
  downloadGroup: ({
    collection = 'contract',
    locale,
    downloadRateFormat,
    forAL,
    docIds,
  }) => {
    const qs = queryString.stringify({
      locale,
      collection,
      downloadRateFormat,
    });
    const url = `/contracting/download-group?${qs}`;
    const params = { docIds };
    const callFunc = forAL ? callApiForAL : callApi;
    return callFunc(url, 'post', params);
  },
  checkJobUrl: ({ jobUrl, forAL }) => {
    const callFunc = forAL ? callApiForAL : callApi;
    return callFunc(jobUrl, 'get');
  },
};

// Fetch API keys
export const apiKeys = {
  fetchApiKeys: () => callApi('/ai/api-keys', 'get'),
  addApiKey: payload => callApi('/ai/api-keys', 'post', payload),
  removeApiKey: payload => callApi(`/ai/api-keys/${payload.id}`, 'delete'),
}

// Contentful for sidebar

const buildURLQuery = obj =>
  Object.entries(obj)
    .map(pair => pair.map(encodeURIComponent).join('='))
    .join('&');

function contentfulApi({
  accessToken = `${CONTENTFUL_HELPSIDEBAR}`,
  bodyParam = null,
  endpoint,
  environment = 'master',
  method = 'get',
  searchParams = {},
  space = 'wwevjgfp8hg4'
} = {}) {
  searchParams.access_token = accessToken;
  const fullUrl = `${API_CONTENTFUL}spaces/${space}/environments/${environment}/${endpoint}?${buildURLQuery(
    searchParams
  )}`;
  const body = bodyParam ? JSON.stringify(bodyParam) : null;
  return fetch(fullUrl, {
    method,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body
  })
    .then(response => response.json().then(json => ({ json, response })))
    .then(({ json, response }) => {
      if (!response.ok) {
        return Promise.reject(json);
      }
      if (Array.isArray(json)) return json;
      return Object.assign({}, json);
    })
    .then(
      response => ({ response }),
      error => ({ error: error || 'Something bad happened' })
    );
}

export const contentful = {
  sidebarHelp: {
    getAllEntries: () =>
      contentfulApi({
        endpoint: 'entries',
        searchParams: {
          content_type: 'sidebarHelp',
          select: 'sys.id,fields.matchingUrls,fields.matchingRole,fields.title'
        }
      }),
    getHelp: articleId =>
      contentfulApi({
        endpoint: `entries`,
        searchParams: { content_type: 'sidebarHelp', 'sys.id': articleId }
      })
  }
};
